import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { MapPin, Phone, Star, Radio, Clock, Route, Bike, CheckCircle2, Sparkles } from "lucide-react";

// Delhi coords — fallback destination for demos where the claim lacks lat/lng.
const DEFAULT_DEST = { lat: 28.6139, lng: 77.2090 };

export default function SurveyorTracker({ claim }) {
  const [dispatch, setDispatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dispatching, setDispatching] = useState(false);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);

  const load = async () => {
    try {
      const r = await api.get(`/surveyor/dispatch/${claim.claim_id}`);
      setDispatch(r.data);
      setError("");
    } catch (e) {
      if (e.response?.status === 404) setDispatch(null);
      else setError(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [claim.claim_id]);

  // Poll every 5 seconds when there's an active dispatch
  useEffect(() => {
    if (!dispatch || dispatch.status === "Arrived") return;
    const t = setInterval(() => { load(); setTick((n) => n + 1); }, 5000);
    return () => clearInterval(t);
  }, [dispatch?.dispatch_id, dispatch?.status]);

  const startDispatch = async () => {
    setDispatching(true);
    try {
      // If claim has coords use them, else default. In production this comes from geocoding accident.location.
      const dest = claim.accident?.lat && claim.accident?.lng
        ? { lat: claim.accident.lat, lng: claim.accident.lng }
        : DEFAULT_DEST;
      await api.post("/surveyor/dispatch", {
        claim_id: claim.claim_id,
        destination_lat: dest.lat,
        destination_lng: dest.lng,
      });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Dispatch failed");
    } finally {
      setDispatching(false);
    }
  };

  if (loading) return <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">Loading…</div>;

  if (!dispatch) {
    return (
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6" data-testid="surveyor-idle">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E6EEF9] dark:bg-blue-900/30 text-[#003DA5] dark:text-blue-400 flex items-center justify-center">
            <Bike className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-bold text-slate-900 dark:text-white">Surveyor dispatch</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">No surveyor assigned yet. Dispatch one now to see real-time tracking.</div>
          </div>
          <button
            onClick={startDispatch}
            disabled={dispatching}
            className="btn-signature rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-60"
            data-testid="surveyor-dispatch-btn"
          >
            {dispatching ? "Dispatching…" : "Dispatch surveyor"}
          </button>
        </div>
        {error && <div className="mt-3 text-xs text-rose-600">{error}</div>}
      </div>
    );
  }

  const { surveyor, current, destination, origin, remaining_km, remaining_min, progress, status } = dispatch;
  const eta = new Date(dispatch.eta_at);
  const arrived = status === "Arrived";

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden" data-testid="surveyor-tracker">
      {/* Map-ish visual */}
      <div className="relative h-40 bg-gradient-to-br from-[#003DA5] via-[#0055D4] to-[#00A86B] overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-20" />
        {/* Origin marker */}
        <div className="absolute top-4 left-6 text-white/70 text-[10px] font-mono">📍 Start</div>
        {/* Destination marker */}
        <div className="absolute bottom-4 right-6 text-white/90 text-[10px] font-mono">🎯 Your location</div>

        {/* Curved dashed route */}
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 400 160">
          <path d="M40,30 Q200,10 360,130" stroke="rgba(255,255,255,0.45)" strokeDasharray="5 6" strokeWidth="2" fill="none" />
        </svg>

        {/* Moving surveyor dot */}
        <motion.div
          className="absolute w-10 h-10 rounded-full bg-white shadow-2xl flex items-center justify-center border-4 border-[#FF7A00]"
          initial={{ left: "10%", top: "18%" }}
          animate={{
            left: `${10 + (progress / 100) * 80}%`,
            top: `${18 + (progress / 100) * 62}%`,
          }}
          transition={{ duration: 2, ease: "easeOut" }}
          key={tick}
        >
          <Bike className="w-4 h-4 text-[#003DA5]" />
          <span className="absolute -inset-1 rounded-full border-2 border-white/60 animate-pulse-ring" />
        </motion.div>

        {/* Live badge */}
        <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] font-bold uppercase tracking-widest text-white flex items-center gap-1.5">
          <Radio className="w-3 h-3 animate-pulse text-[#FF7A00]" /> Live · {status}
        </div>
      </div>

      {/* Surveyor card */}
      <div className="p-5 flex items-center gap-4 border-b border-slate-100 dark:border-slate-800">
        <img
          src={`https://api.dicebear.com/9.x/avataaars/svg?seed=${surveyor.photo_seed || surveyor.name}`}
          alt={surveyor.name}
          className="w-14 h-14 rounded-full border-2 border-slate-200 dark:border-slate-700 bg-white"
        />
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-slate-900 dark:text-white">{surveyor.name}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {surveyor.rating}</span>
            <span>· {surveyor.vehicle}</span>
            <span className="font-mono">· {surveyor.surveyor_id}</span>
          </div>
        </div>
        <a
          href={`tel:${surveyor.phone}`}
          className="w-11 h-11 rounded-full bg-[#00A86B] text-white flex items-center justify-center hover:opacity-90"
          aria-label={`Call ${surveyor.name}`}
          data-testid="surveyor-call"
        >
          <Phone className="w-4 h-4" />
        </a>
      </div>

      {/* Live stats */}
      <div className="grid grid-cols-3 gap-3 p-5">
        <StatMini icon={Clock} label="ETA" value={arrived ? "Arrived" : `${remaining_min} min`} tone="text-[#FF7A00]" />
        <StatMini icon={Route} label="Distance" value={arrived ? "0.0 km" : `${remaining_km} km`} tone="text-[#003DA5]" />
        <StatMini icon={arrived ? CheckCircle2 : Sparkles} label="Progress" value={`${Math.round(progress)}%`} tone="text-[#00A86B]" />
      </div>

      <div className="px-5 pb-5">
        <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-[#003DA5] via-[#FF7A00] to-[#00A86B] rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
            data-testid="surveyor-progress"
          />
        </div>
        <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-1.5 flex items-center gap-1.5">
          <MapPin className="w-3 h-3" /> Expected by {eta.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
    </div>
  );
}

function StatMini({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50 p-3">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
        <Icon className={`w-3 h-3 ${tone}`} /> {label}
      </div>
      <div className="font-heading font-bold text-lg text-slate-900 dark:text-white mt-0.5">{value}</div>
    </div>
  );
}
